/**
 * Catering Packages API — packages with tags + variants.
 *
 * `locationId` is a query param on GET/DELETE and a body field on POST/PATCH (same as every
 * other catering call). Errors surface as `CateringApiError` (`message`, `code`, `status`).
 * Tags are NOT managed here — they live in catering settings (`catering_package_tags`).
 */
import { axiosFetch as authFetch } from './axiosFetch';
import { CATERING_BASE_URL, buildHeaders, cateringFetch, handleOk } from './cateringApi';
import { normalizePackageItems } from '../components/catering/utils/packageItems';
import type {
  CateringPackage,
  PackageCatalogItem,
  PackageCatalogTray,
  PackageMedia,
  PackageTag,
  PackageVariant,
} from '../components/catering/data/types';

const BASE = `${CATERING_BASE_URL}/api/v1/catering`;

// ── Wire shapes ─────────────────────────────────────────────────────────────────

/** A package item as sent to the server: catalogue/known row (`itemId`) or new custom row (`name`). */
export interface PackageItemInput {
  itemId?: string;
  name?: string;
  isCustom?: boolean;
  /** Catalogue items only. `null` = Individual; a tray id must belong to this item (BE returns 400 otherwise). */
  tierId?: string | null;
  /** Custom items: the full size set. Omitted = sizes unchanged; `[]` = no sizes. Sizes not listed are removed. */
  tiers?: PackageCustomTierInput[];
  /** Quantity per serving — defaults to 1 server-side when omitted. Applies to catalogue and custom items. */
  qty?: number;
  unit?: string | null;
  /** Non-negative integer. BE sorts by it (ties → array position) and stores it renumbered 0..n-1; omitted → array position. */
  sortOrder?: number;
}

/** Custom-item size on the wire. `id` omitted for a new size (the server generates one). */
export interface PackageCustomTierInput {
  id?: string;
  label: string;
  servesCount: number;
  price: number;
  /** Position in the list being sent (0..n-1); the server renumbers its saved tiers to match. */
  sortOrder?: number;
}

/**
 * One variant on the wire. `id` = update that variant, omitted = new. The list sent on PATCH is a whole-set
 * replace: a live variant missing from it is removed. Exactly one entry must be `isDefault` (the BE would
 * otherwise silently make the FIRST one the default, so always send the flag explicitly).
 * `packageItems` omitted = items untouched; `[]` = clear; a list = replace.
 */
export interface PackageVariantInput {
  id?: string;
  /** Optional, max 100. Blank/null = no name (the UI shows `serves`). */
  name?: string | null;
  /** Text, max 30 (`"25"`, `"10-20"`). Must be a JSON string. */
  serves: string;
  /** Number ≥ 0. */
  price: number;
  isDefault: boolean;
  /** Renumbered 0..n-1 server-side; sent as the array index. */
  sortOrder?: number;
  packageItems?: PackageItemInput[];
  /** This serving option's notes, max 500. Blank/null = none (the variant list is a full set, so it is always sent). */
  notes?: string | null;
}

export interface CreatePackageBody {
  locationId: string;
  name: string;
  notes?: string | null;
  minQtyRequired?: number | null;
  maxQtyRequired?: number | null;
  /** Ids from the settings tag list; unknown id → 400 INVALID_PACKAGE_TAG. */
  tagIds?: string[];
  /** 1..20 entries. A package with no extra options is just one (default) variant. */
  variants: PackageVariantInput[];
  /** `YYYY-MM-DD`. Omitted = no limit. BE → 400 if `From` > `To` or `orderStartingDate` > `To`; it does not enforce them on estimates/orders. */
  orderStartingDate?: string;
  packageAvailabilityFrom?: string;
  packageAvailabilityTo?: string;
}

/** PATCH semantics: omitted = unchanged. `tagIds` and `variants` are whole-set replaces when present. */
export interface UpdatePackageBody {
  locationId: string;
  name?: string;
  notes?: string | null;
  minQtyRequired?: number | null;
  maxQtyRequired?: number | null;
  /** Position in the location list; stored exactly as sent (the caller renumbers). */
  sortOrder?: number;
  /** `[]` removes every tag. */
  tagIds?: string[];
  variants?: PackageVariantInput[];
  /** `YYYY-MM-DD`; omitted = unchanged, `null` = clear (no limit). */
  orderStartingDate?: string | null;
  packageAvailabilityFrom?: string | null;
  packageAvailabilityTo?: string | null;
}

// ── Normalisers (BE numbers can arrive as strings/nulls) ─────────────────────────

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/** A calendar date as `YYYY-MM-DD`. Tolerates a full ISO timestamp (keeps the date part); anything else → null. */
export function ymd(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const m = /^(\d{4}-\d{2}-\d{2})(?:$|T)/.exec(v.trim());
  return m ? m[1] : null;
}

/** `mediaResponseList` → images, in the order given (the BE sorts by `sortOrder`). Rows without a file name are dropped. */
export function normalizePackageMedia(raw: unknown): PackageMedia[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r, index) => {
    const o = (r ?? {}) as Record<string, unknown>;
    const id = o.id != null ? String(o.id) : '';
    const fileName = typeof o.fileName === 'string' ? o.fileName.trim() : '';
    return id && fileName ? [{ id, fileName, type: str(o.type) ?? '', sortOrder: num(o.sortOrder) ?? index }] : [];
  });
}

export function normalizePackageTags(raw: unknown): PackageTag[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r) => {
    const o = (r ?? {}) as Record<string, unknown>;
    const id = o.id != null ? String(o.id) : '';
    const value = typeof o.value === 'string' ? o.value : '';
    return id && value ? [{ id, value }] : [];
  });
}

export function normalizePackageVariant(raw: unknown, index = 0): PackageVariant {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    id: String(o.id ?? ''),
    name: str(o.name),
    serves: o.serves != null ? String(o.serves) : '',
    price: num(o.price) ?? 0,
    isDefault: o.isDefault === true,
    sortOrder: num(o.sortOrder) ?? index,
    packageItems: normalizePackageItems(o.packageItems),
    // A blank note is no note (the list hides the Notes card for null).
    notes: str(o.notes)?.trim() ? str(o.notes) : null,
  };
}

export function normalizePackage(raw: unknown): CateringPackage {
  const o = (raw ?? {}) as Record<string, unknown>;
  // The BE already orders variants by `sortOrder`; keep the order given.
  const variants = Array.isArray(o.variants) ? o.variants.map((v, i) => normalizePackageVariant(v, i)) : [];
  const defaultVariantId =
    str(o.defaultVariantId) ?? variants.find((v) => v.isDefault)?.id ?? variants[0]?.id ?? null;
  return {
    id: String(o.id ?? ''),
    name: str(o.name) ?? '',
    notes: str(o.notes),
    minQtyRequired: num(o.minQtyRequired),
    maxQtyRequired: num(o.maxQtyRequired),
    tags: normalizePackageTags(o.tags),
    sortOrder: num(o.sortOrder),
    defaultVariantId,
    variants,
    orderStartingDate: ymd(o.orderStartingDate),
    packageAvailabilityFrom: ymd(o.packageAvailabilityFrom),
    packageAvailabilityTo: ymd(o.packageAvailabilityTo),
    media: normalizePackageMedia(o.mediaResponseList),
    ...(str(o.createdAt) ? { createdAt: str(o.createdAt) as string } : {}),
    ...(str(o.updatedAt) ? { updatedAt: str(o.updatedAt) as string } : {}),
  };
}

/** Package POST/PATCH/duplicate respond `{ success, package }`. */
function unwrapPackage(json: unknown): unknown {
  return (json as { package?: unknown } | null)?.package;
}

// ── Packages ───────────────────────────────────────────────────────────────────

/** `tagId` (optional) filters to packages carrying that tag. */
export const listPackagesApi = async (
  locationId: string,
  token: string | null,
  tagId?: string,
): Promise<CateringPackage[]> => {
  const params = new URLSearchParams({ locationId });
  if (tagId) params.set('tagId', tagId);
  const res = await cateringFetch(`${BASE}/packages?${params}`, { headers: buildHeaders(token) });
  const json = await handleOk<{ packages?: unknown[] } | unknown[]>(res, 'Failed to load packages');
  const list = Array.isArray(json) ? json : json?.packages ?? [];
  return list.map(normalizePackage);
};

/**
 * One package for the details / edit screen: `GET /catering/packages/:id?locationId=` (required — 400 without it).
 * Same shape as an entry of the list. 404 `NOT_FOUND` when it doesn't exist, was deleted, or belongs to another
 * location. The list endpoint is for list views only.
 */
export const getPackageApi = async (
  id: string,
  locationId: string,
  token: string | null,
): Promise<CateringPackage> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE}/packages/${encodeURIComponent(id)}?${params}`, { headers: buildHeaders(token) });
  const json = await handleOk<unknown>(res, 'Failed to load the package');
  const raw = unwrapPackage(json);
  if (!raw || typeof raw !== 'object') {
    // A 200 without a package would otherwise normalise into an empty package with no id.
    throw Object.assign(new Error('Package not found'), { code: 'NOT_FOUND', status: 404 });
  }
  return normalizePackage(raw);
};

export const createPackageApi = async (
  body: CreatePackageBody,
  token: string | null,
): Promise<CateringPackage> => {
  const res = await cateringFetch(`${BASE}/packages`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  const json = await handleOk<unknown>(res, 'Failed to create package');
  return normalizePackage(unwrapPackage(json));
};

export const updatePackageApi = async (
  id: string,
  body: UpdatePackageBody,
  token: string | null,
): Promise<CateringPackage> => {
  const res = await cateringFetch(`${BASE}/packages/${id}`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  const json = await handleOk<unknown>(res, 'Failed to update package');
  return normalizePackage(unwrapPackage(json));
};

export const duplicatePackageApi = async (
  id: string,
  body: { locationId: string; name?: string },
  token: string | null,
): Promise<CateringPackage> => {
  const res = await cateringFetch(`${BASE}/packages/${id}/duplicate`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  const json = await handleOk<unknown>(res, 'Failed to duplicate package');
  return normalizePackage(unwrapPackage(json));
};

export const deletePackageApi = async (
  id: string,
  locationId: string,
  token: string | null,
): Promise<void> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE}/packages/${id}?${params}`, {
    method: 'DELETE',
    headers: buildHeaders(token),
  });
  await handleOk<unknown>(res, 'Failed to delete package');
};

// ── Package item picker (catalogue items + tray sizes) ───────────────────────────

function normalizeCatalogTray(raw: unknown, index: number): PackageCatalogTray {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    id: String(o.id ?? ''),
    label: str(o.label) ?? '',
    servesCount: num(o.servesCount) ?? 0,
    price: num(o.price) ?? 0,
    sortOrder: num(o.sortOrder) ?? index,
  };
}

export function normalizePackageCatalogItem(raw: unknown): PackageCatalogItem {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    id: String(o.id ?? ''),
    name: str(o.name) ?? '',
    basePrice: num(o.basePrice) ?? 0,
    individualServesCount: num(o.individualServesCount),
    minOrderQty: num(o.minOrderQty),
    maxOrderQty: num(o.maxOrderQty),
    trays: Array.isArray(o.trays) ? o.trays.map(normalizeCatalogTray) : [],
    isActive: o.isActive !== false,
    updatedAt: str(o.updatedAt) ?? '',
  };
}

/** Catering-enabled, active catalogue items only (no custom or add-on items). One call, no pagination in v1. */
export const listPackageCatalogItemsApi = async (
  locationId: string,
  token: string | null,
): Promise<PackageCatalogItem[]> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE}/packages/catalog-items?${params}`, { headers: buildHeaders(token) });
  const json = await handleOk<{ items?: unknown[] } | unknown[]>(res, 'Failed to load catalogue items');
  const list = Array.isArray(json) ? json : json?.items ?? [];
  return list.map(normalizePackageCatalogItem);
};

// ── Package images ──────────────────────────────────────────────────────────────

/** `POST /packages/upload-image` response. */
export interface PackageImageUploadResult {
  packageId: string;
  imageId: string;
  status?: string;
  message?: string;
}

/**
 * Uploads one image to a package (`packageId` = the package's own id): multipart `FormData`, file in field `image`.
 * Goes through `authFetch` with a Bearer token ONLY — exactly like `uploadItemImageApi` — and never through
 * `cateringFetch`, whose HMAC wrapper replaces any non-string body with an empty signed envelope.
 * Don't set `Content-Type`: the browser adds the multipart boundary.
 */
export const uploadPackageImageApi = async (
  file: File | Blob,
  packageId: string,
  token: string | null,
  sortOrder = 1,
): Promise<PackageImageUploadResult> => {
  const params = new URLSearchParams({ packageId, sortOrder: String(sortOrder) });
  const form = new FormData();
  form.append('image', file, file instanceof File ? file.name : 'image');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await authFetch(`${BASE}/packages/upload-image?${params}`, { method: 'POST', headers, body: form });
  const json = await handleOk<Record<string, unknown>>(res, 'Failed to upload the image');
  return {
    packageId: String(json?.packageId ?? packageId),
    imageId: String(json?.imageId ?? ''),
    ...(typeof json?.status === 'string' ? { status: json.status } : {}),
    ...(typeof json?.message === 'string' ? { message: json.message } : {}),
  };
};

/** `DELETE /packages/remove-image?imageId=` — Bearer only, like `removeItemImageApi`. */
export const removePackageImageApi = async (imageId: string, token: string | null): Promise<void> => {
  const params = new URLSearchParams({ imageId });
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await authFetch(`${BASE}/packages/remove-image?${params}`, { method: 'DELETE', headers });
  // Success may have no (JSON) body — only a failure needs parsing, to surface the server's message.
  if (!res.ok) await handleOk<unknown>(res, 'Failed to remove the image');
};
